import { Button } from '../../../../../../components/button/button';
import { Card } from '../../../../../../components/card/card';
import { Checkbox } from '../../../../../../components/checkbox/checkbox';
import { Container } from '../../../../../../components/container/container';
import { InputFileSquare } from '../../../../../../components/input-file/input-file-square/input-file-square';
import { Input } from '../../../../../../components/input/input';
import { Select } from '../../../../../../components/select/select';
import { LangInput } from '../../../../../../features/lang-input/lang-input';
import { LangSelect } from '../../../../../../features/lang-select/lang-select';
import { MultiSelect } from '../../../../../../features/multi-select/multi-select';
import { findItemById, replaceItemById } from '../../../../../../helpers/finders';
import { validateLang } from '../../../../../../helpers/lang';
import { hasService } from '../../../../../../helpers/local-storage';
import { getValueFromOptions } from '../../../../../../helpers/mappers';
import { validate } from '../../../../../../helpers/validate';
import { useLang } from '../../../../../../hooks/use-lang';
import { TreeType } from '../../../../../../store/api/trees-api';
import { useFormStore } from '../../../../../../store/use-form-store';
import { useImageStore } from '../../../../../../store/use-image-store';
import { useInputLangStore } from '../../../../../../store/use-input-lang-store';
import { useTagStaffStore } from '../../../../../../store/use-tag-staff-store';
import { useTreeStore } from '../../../../../../store/use-tree-store';
import s from './tree-node.module.css';

export type TreeNodeCheckTypes =
  | 'is_photo_required'
  | 'is_hot_access'
  | 'is_hidden'
  | 'is_callback';

const checks: Record<TreeNodeCheckTypes, string> = {
  is_photo_required: 'Фото в отчете обязательно',
  is_hot_access: 'Использовать как ярлык',
  is_hidden: 'Скрыть из приложения пользователя',
  is_callback: 'Собирать обратную связь',
};

export const TreeNode = () => {
  const { t, getLocale } = useLang();
  const { handleUpdateNode, node, handleUpdateTree, handleResetNode, tree } = useTreeStore();
  const { list } = useFormStore();
  const { tags } = useTagStaffStore();
  const { language } = useInputLangStore();
  const { postImage, isLoading } = useImageStore();

  const selectedTags = getValueFromOptions(tags, node?.tags?.map((item) => item.id) || []);
  const isExist = findItemById(tree?.nodes, node?.id);

  const handleCreate = () => {
    const isValid = validate({
      nodeName: { rule: () => validateLang(node?.name) },
    });
    if (!isValid || !node) return;

    if (isExist) {
      const updatedTree = replaceItemById(tree?.nodes, node.id, node) as Partial<TreeType>[];
      handleUpdateTree({ nodes: updatedTree });
    } else {
      node.id = Math.random();
      handleUpdateTree({ nodes: [...(tree?.nodes || []), node] });
    }
    handleResetNode();
  };

  const handleChangeImage = (file?: File[]) => {
    if (!file) {
      handleUpdateNode({ images: undefined });
      return;
    }
    const formData = new FormData();
    formData.append('image', file[0]);
    postImage(formData).then((res) =>
      handleUpdateNode({
        images: { ...node?.images, main: { thumbnail: res?.normal }, other: [] },
      }),
    );
  };

  return (
    <Card className={s.feature}>
      <div className={s.title}>
        <h3>2. {t('Основные настройки')}</h3>
        <LangSelect />
      </div>

      <LangInput
        value={node?.name}
        onChange={(e) => handleUpdateNode({ name: e })}
        name="nodeName"
        data-testid="node-name"
        placeholder={t('Название элемента')}
      />
      <ul className={s.checks}>
        {Object.entries(checks).map(([key, value]) => {
          const keyAsType = key as TreeNodeCheckTypes;

          return (
            <Checkbox
              key={key}
              id={`node-check-${key}`}
              label={t(value)}
              onClick={() => handleUpdateNode({ [keyAsType]: !node?.[keyAsType] })}
              checked={node?.[keyAsType]}
            />
          );
        })}
      </ul>
      <div className={s.inputs}>
        <Input
          value={node?.action_text}
          onChange={(e) => handleUpdateNode({ action_text: e.currentTarget.value })}
          data-testid="node-action_text"
          placeholder={t('Введите шаблон текста')}
        />
        <MultiSelect
          options={tags || []}
          onSelect={(e) => handleUpdateNode({ tags: e })}
          value={selectedTags}
          data-testid="node-groups"
          placeholder={t('Группа ответственных')}
        />
        <Input
          onChange={(e) =>
            handleUpdateNode({ notification_repeat_time: Number(e.currentTarget.value) })
          }
          type="number"
          value={node?.notification_repeat_time}
          data-testid="notification_repeat_time"
          placeholder={t('Интервал повтора уведомления')}
          min={0}
        />
        <Input
          type="number"
          value={node?.deadline_minutes || undefined}
          onChange={(e) => handleUpdateNode({ deadline_minutes: e.currentTarget.value || null })}
          data-testid="node-deadline_minutes"
          placeholder={t('Время выполнения (мин)')}
          min={0}
        />
      </div>

      {hasService('form') && (
        <Container label={t('Форма')}>
          <Select
            data-testid="form-select"
            options={
              list?.map((item) => ({ id: item.uuid, value: getLocale(item.name, language) })) || []
            }
            onSelect={(e) =>
              handleUpdateNode({ form_uuid: e.id, form: list?.find((item) => item.uuid === e.id) })
            }
            value={
              node?.form
                ? { id: node.form.uuid, value: getLocale(node.form.name, language) }
                : undefined
            }
          />
        </Container>
      )}

      <Container label={t('Изображение')}>
        <InputFileSquare
          types={['image/*']}
          src={node?.images?.main?.thumbnail}
          wrapperClassName={s.image}
          onChange={handleChangeImage}
          isLoading={isLoading}
          name="img"
          deletable={!!node?.images?.main?.thumbnail}
          data-testid="image-input"
        />
      </Container>

      <Button onClick={handleCreate} className={s.button} data-testid="node-submit">
        {isExist ? t('Изменить') : t('Создать')}
      </Button>
    </Card>
  );
};
